import { useMutation, useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Check from "@mui/icons-material/Check";
import EditNote from "@mui/icons-material/EditNote";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import { RevisionCandidatesQuery, TierIiReportEncampStatus } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { encampStatusToLabel, orgStatusToLabel } from "util/constants";

import { useBulkActionContext } from ".";

const REVISION_CANDIDATES_QUERY = gql(`
  query RevisionCandidates($facilityIds: [ID!]!, $reportingYear: Int!) {
    revisionCandidates(facilityIds: $facilityIds, reportingYear: $reportingYear) {
      facilityId
      facilityName
      organization
      state
      annualReportId
      annualReportEncampStatus
      annualReportOrgStatus
      hasOpenRevision
      isEligible
      ineligibilityReason
    }
  }
`);

const CREATE_REVISION_REPORTS = gql(`
  mutation CreateRevisionReports($reportingYear: Int!, $facilityIds: [ID!]!, $reasonForReport: String) {
    createTierIIReports(
      reportingYear: $reportingYear
      reportKind: REVISION
      facilityIds: $facilityIds
      reasonForReport: $reasonForReport
    ) {
      id
      facilityId
    }
  }
`);

type ReasonFormState = {
  reasonForReport: string;
};

export function OpenRevisionMenuItem() {
  const { setDialogState } = useBulkActionContext();

  return (
    <MenuItem onClick={() => setDialogState("openRevisions")}>
      <ListItemIcon>
        <EditNote />
      </ListItemIcon>
      <ListItemText>Open Revision Reports</ListItemText>
    </MenuItem>
  );
}

export function OpenRevisionDialogs() {
  const {
    dialogState,
    onClose,
    selectedRows,
    reportingYear: contextReportingYear,
  } = useBulkActionContext();
  const [step, setStep] = useState<"reason" | "confirm">("reason");
  const [reasonForReport, setReasonForReport] = useState("");

  const handleReasonContinue = useCallback((reason: string) => {
    setReasonForReport(reason);
    setStep("confirm");
  }, []);

  const handleBack = useCallback(() => {
    setStep("reason");
  }, []);

  const handleClose = useCallback(() => {
    setStep("reason");
    setReasonForReport("");
    onClose();
  }, [onClose]);

  if (dialogState !== "openRevisions") {
    return null;
  }

  const reportingYear =
    contextReportingYear > 0 ? contextReportingYear : currentTierIIReportingYear;

  const facilityIds = selectedRows.filter((row) => !!row.facilityId).map((row) => row.facilityId);

  if (step === "reason") {
    return (
      <RevisionReasonDialog
        onClose={handleClose}
        onContinue={handleReasonContinue}
        initialReason={reasonForReport}
      />
    );
  }

  return (
    <ConfirmRevisionDialog
      reportingYear={reportingYear}
      facilityIds={facilityIds}
      reasonForReport={reasonForReport}
      onClose={handleClose}
      onBack={handleBack}
    />
  );
}

function RevisionReasonDialog({
  onClose,
  onContinue,
  initialReason,
}: {
  onClose: () => void;
  onContinue: (reason: string) => void;
  initialReason: string;
}) {
  const form = useForm<ReasonFormState>({
    defaultValues: {
      reasonForReport: initialReason,
    },
    mode: "onChange",
  });

  const { handleSubmit, watch } = form;
  const reasonForReport = watch("reasonForReport");

  const onSubmit = handleSubmit((data) => {
    onContinue(data.reasonForReport.trim());
  });

  const trimmedReason = reasonForReport?.trim() || "";
  const isValid = trimmedReason.length > 0 && trimmedReason.length <= 400;

  return (
    <FormProvider {...form}>
      <Dialog
        open
        maxWidth="md"
        onClose={onClose}
        title="Open Revision Reports"
        primaryActionButtonProps={{
          onClick: onSubmit,
          saveText: "CONTINUE",
          endIcon: <ArrowForward />,
          disabled: !isValid,
        }}
      >
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Please enter a reason for revision. This will be submitted along with the revisions in the
          portal where required.
        </Typography>

        <FormTextField
          name="reasonForReport"
          label="Reason for Revision *"
          fullWidth
          rules={{
            required: "Reason for revision is required",
            validate: (value: string) => {
              const trimmed = value?.trim() || "";
              if (trimmed.length > 400) {
                return "Reason for revision must be no more than 400 characters";
              }
              return true;
            },
          }}
          textFieldProps={{
            multiline: true,
            rows: 4,
            variant: "outlined",
            autoFocus: true,
          }}
        />
      </Dialog>
    </FormProvider>
  );
}

type RevisionCandidate = RevisionCandidatesQuery["revisionCandidates"][number];

function ConfirmRevisionDialog({
  reportingYear,
  facilityIds,
  reasonForReport,
  onClose,
  onBack,
}: {
  reportingYear: number;
  facilityIds: string[];
  reasonForReport: string;
  onClose: () => void;
  onBack: () => void;
}) {
  const theme = useTheme();
  const alerts = useAlerts();
  const [acknowledgeIncomplete, setAcknowledgeIncomplete] = useState(false);

  const { data, loading } = useQuery(REVISION_CANDIDATES_QUERY, {
    variables: {
      facilityIds,
      reportingYear,
    },
    fetchPolicy: "network-only",
  });

  const [createRevisions, { loading: isCreating }] = useMutation(CREATE_REVISION_REPORTS, {
    onCompleted(result) {
      const count = result.createTierIIReports.length;
      alerts.success(`Successfully created ${count} revision ${pluralize("report", count)}.`);
      onClose();
    },
    onError(error) {
      alerts.error("Error creating revision reports", error);
    },
  });

  const candidates = data?.revisionCandidates ?? [];

  const eligibleCandidates = useMemo(
    () => candidates.filter((c) => c.isEligible),
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [candidates]
  );

  const hasIneligibleCandidates = candidates.some((c) => !c.isEligible);

  const hasNonFilingCompleteCandidates = useMemo(
    () =>
      eligibleCandidates.some(
        (c) => c.annualReportEncampStatus !== TierIiReportEncampStatus.FilingComplete
      ),
    [eligibleCandidates]
  );

  const canSubmit = useMemo(() => {
    if (eligibleCandidates.length === 0) {
      return false;
    }
    if (hasNonFilingCompleteCandidates && !acknowledgeIncomplete) {
      return false;
    }
    return true;
  }, [eligibleCandidates.length, hasNonFilingCompleteCandidates, acknowledgeIncomplete]);

  const handleSubmit = useCallback(async () => {
    const eligibleFacilityIds = eligibleCandidates.map((c) => c.facilityId);
    await createRevisions({
      variables: {
        reportingYear,
        facilityIds: eligibleFacilityIds,
        reasonForReport,
      },
    });
  }, [createRevisions, eligibleCandidates, reportingYear, reasonForReport]);

  if (loading) {
    return (
      <Dialog open onClose={onClose} title="Open Revision Reports">
        <Typography>Loading...</Typography>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      maxWidth="lg"
      onClose={onClose}
      title="Open Revision Reports"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        saveText: `Create ${eligibleCandidates.length} ${pluralize(
          "Revision",
          eligibleCandidates.length
        )}`,
        loading: isCreating,
        disabled: !canSubmit,
      }}
      secondaryActionElements={[
        <Button key="back" onClick={onBack}>
          Back
        </Button>,
      ]}
    >
      <Typography variant="body1" sx={{ mb: 2 }}>
        You are about to create{" "}
        <strong>
          {eligibleCandidates.length} revision {pluralize("report", eligibleCandidates.length)}
        </strong>{" "}
        for the following facilities in reporting year {reportingYear}.
      </Typography>

      {hasIneligibleCandidates && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: `1px solid ${theme.palette.warning.main}`,
            borderRadius: 1,
            backgroundColor: theme.palette.warning.light + "20",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <WarningAmber color="warning" />
            <Typography variant="body2" fontWeight={500}>
              Some facilities are not eligible for revision
            </Typography>
          </Box>
          <Typography variant="body2">
            {candidates.length - eligibleCandidates.length}{" "}
            {pluralize("facility", candidates.length - eligibleCandidates.length)} will be skipped.
            See the table below for details.
          </Typography>
        </Box>
      )}

      <DataGrid
        columns={[
          {
            field: "organization",
            headerName: "Organization",
            flex: 1,
          },
          {
            field: "facilityName",
            headerName: "Facility",
            flex: 1,
          },
          {
            field: "state",
            headerName: "State/Territory",
            flex: 0.4,
          },
          {
            field: "annualReportStatus",
            headerName: "Annual Report Status",
            flex: 1,
            renderCell: (params) => {
              const row = params.row as RevisionCandidate;
              if (!row.annualReportId) {
                return <Typography color="text.secondary">No report</Typography>;
              }
              const orgStatus = row.annualReportOrgStatus
                ? orgStatusToLabel(row.annualReportOrgStatus)
                : "";
              const encampStatus = row.annualReportEncampStatus
                ? encampStatusToLabel(row.annualReportEncampStatus)
                : "";
              return (
                <Box>
                  <Typography variant="body2">{orgStatus}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {encampStatus}
                  </Typography>
                </Box>
              );
            },
          },
          {
            field: "isEligible",
            headerName: "Eligible",
            flex: 0.8,
            renderCell: (params) => {
              const row = params.row as RevisionCandidate;
              if (row.isEligible) {
                return <Check color="success" />;
              }
              return (
                <Typography variant="body2" color="error">
                  {row.ineligibilityReason}
                </Typography>
              );
            },
          },
        ]}
        rows={candidates}
        getRowId={(row) => row.facilityId}
        getRowHeight={() => "auto"}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          "& .MuiDataGrid-cell": {
            py: 1,
          },
        }}
      />

      {hasNonFilingCompleteCandidates && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledgeIncomplete}
                onChange={(e) => setAcknowledgeIncomplete(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I acknowledge that this will create revision reports even though not all reports
                have completed annual reports (FILING_COMPLETE status)
              </Typography>
            }
          />
        </Box>
      )}
    </Dialog>
  );
}
